/**
 * Résolution du collaborateur affecté à un véhicule.
 *
 * POURQUOI CE FICHIER EXISTE
 * --------------------------
 * Un refactor backend a déplacé `firstname`, `lastname` et `userId` de la
 * racine de la réponse vers un objet imbriqué `assignedUser` (voir
 * `FleetService::transformFleetVehicleData`). `Collaborator.tsx` a été corrigé
 * pour lire la nouvelle forme ; `Mileage.tsx` ne l'a pas été et testait encore
 * `vehicule?.userId`.
 *
 * Conséquence : sur tout backend à jour, `vehicule.userId` valait toujours
 * `undefined`, donc le bouton « Relancer le collaborateur » était désactivé en
 * permanence — y compris sur un véhicule bel et bien affecté. Le clic ne
 * partait jamais, et comme le style désactivé n'était pas distinguable, le
 * bouton avait l'air de ne rien faire.
 *
 * La règle de lecture vit désormais à un seul endroit. Un troisième écran qui
 * aura besoin du collaborateur affecté importera ceci plutôt que de
 * redécouvrir la forme de la réponse — et le bug avec.
 */

type AssignedUserPayload = {
  userId?: number | null;
  firstname?: string | null;
  lastname?: string | null;
  department?: string | null;
};

export type AssignedCollaborator = {
  userId: number | null;
  firstname: string;
  lastname: string;
  /** Nom affichable, chaîne vide si aucun collaborateur. */
  fullName: string;
  /** Vrai dès qu'une trace d'affectation existe (id OU nom). */
  hasCollaborator: boolean;
};

/**
 * Lit d'abord la forme imbriquée (contrat actuel), puis retombe sur les champs
 * à plat, pour rester compatible avec les déploiements qui n'exposent encore
 * que l'ancienne forme.
 */
export const getAssignedCollaborator = (
  vehicule: unknown
): AssignedCollaborator => {
  const v = vehicule as
    | ({ assignedUser?: AssignedUserPayload } & AssignedUserPayload)
    | null
    | undefined;

  const assignedUser = v?.assignedUser;

  const firstname = assignedUser?.firstname ?? v?.firstname ?? "";
  const lastname = assignedUser?.lastname ?? v?.lastname ?? "";
  const userId = assignedUser?.userId ?? v?.userId ?? null;

  return {
    userId: userId ?? null,
    firstname,
    lastname,
    fullName: `${firstname} ${lastname}`.trim(),
    // Un id SUFFIT, et un nom SUFFIT : certaines réponses portent l'un sans
    // l'autre. Exiger les deux ferait réapparaître la même classe de bug.
    hasCollaborator: Boolean(firstname || lastname || userId),
  };
};
